// filepath: src/components/Features.jsx
import React from "react";
import { useTranslation } from "react-i18next";
import { 
    Container, 
    Box, 
    Grid, 
    Typography, 
    Paper, 
    Button,
    useTheme,
    useMediaQuery,
    styled
} from "@mui/material";
import { 
    Speed, 
    SettingsBackupRestore, 
    AccessTime, 
    Dashboard, 
    History, 
    Security 
} from "@mui/icons-material";
import { motion } from "framer-motion";

// --- AI Kernel Imports ---
import { useKernel } from "./Shortener/hooks/AIKernel";
import { useFrustrationSensor } from "./Shortener/hooks/useFrustrationSensor";

// --- Framer Motion Components ---
const MotionPaper = styled(motion(Paper))({});
const MotionButton = motion(Button);

// --- ANIMATION VARIANTS ---
const headerEnter = {
  hidden: { opacity: 0, y: -20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const itemEnter = {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: { 
        opacity: 1, 
        y: 0, 
        scale: 1, 
        transition: { 
            duration: 0.5, 
            ease: [0.4, 0, 0.2, 1] 
        } 
    },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1, 
    transition: { staggerChildren: 0.15, delayChildren: 0.2 } 
  },
};

const FeatureIcons = [
    <Speed />, 
    <SettingsBackupRestore />, 
    <AccessTime />, 
    <Dashboard />, 
    <History />, 
    <Security /> 
];


const Features = () => {
  // --- AI Engine Integration ---
  const { agentState, reportBehavior } = useKernel();
  useFrustrationSensor(); // 🧠 React to rapid/frustrated clicks on the features grid

  const { t } = useTranslation();
  const featuresList = t("features.list", { returnObjects: true });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  // 🧠 AI Actuator: Flatten UI if user shows high cognitive load
  const shouldSimplify = agentState.ui.simplifyAnimations;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={agentState.theme} // 🧠 Apply Dynamic AI Theme
      style={{
        // We leave the base gradient, but let the AI theme override if it injects dark mode classes
        background: agentState.theme === "theme-cinematic-dark"
          ? "radial-gradient(ellipse at 50% 0%, rgba(173,135,72,0.12), transparent 55%), #0b0c0f"
          : "linear-gradient(135deg, #f3f4f6, #e0e7ff)",
        minHeight: "100vh",
      }}
    >
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        
        {/* Header */}
        <motion.div 
            variants={headerEnter} 
            initial={shouldSimplify ? "visible" : "hidden"} 
            animate="visible"
        >
          <Box textAlign="center" mb={{ xs: 6, md: 10 }}>
            <Typography variant={isMobile ? "h4" : "h3"} fontWeight="bold" gutterBottom>
              {t("features.header.title")}
            </Typography>
            <Typography variant={isMobile ? "body1" : "h6"} color="text.secondary" maxWidth={800} mx="auto">
              {t("features.header.subtitle")}
            </Typography>
          </Box>
        </motion.div>

        {/* Features Grid - Scroll Reveal Orchestration */}
        <motion.div 
            variants={staggerContainer} 
            initial={shouldSimplify ? "visible" : "hidden"} 
            whileInView="visible" 
            viewport={{ once: true, amount: isTablet ? 0.1 : 0.3 }} 
        >
          <Grid container spacing={{ xs: 4, md: 5 }}>
            {featuresList.map((feature, index) => (
              <Grid item xs={12} sm={6} lg={4} key={index}>
                <motion.div variants={itemEnter}>
                  <MotionPaper 
                    elevation={5}
                    // 🧠 AI Sensor: Track exactly which feature caught their attention
                    onClick={() => reportBehavior(`intent_feature_${index}`, 2, 'interest')}
                    
                    // 🧠 AI Actuator: Disable hover bounce if user is frustrated
                    whileHover={!shouldSimplify ? { 
                        scale: 1.03, 
                        boxShadow: "0 15px 30px rgba(0, 0, 0, 0.15)", 
                        y: -5 
                    } : {}}
                    whileTap={!shouldSimplify ? { scale: 0.98 } : {}}
                    
                    sx={{
                      p: { xs: 3, md: 4 },
                      borderRadius: 3,
                      textAlign: "left",
                      minHeight: isMobile ? 'auto' : 240, 
                      bgcolor: "background.paper",
                      cursor: "pointer", // Hint that it's interactive for the AI tracker
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 56,
                        height: 56,
                        bgcolor: theme.palette.primary.light, 
                        borderRadius: "50%", 
                        mb: 2,
                        color: theme.palette.primary.contrastText, 
                      }}
                    >
                      {React.cloneElement(FeatureIcons[index % FeatureIcons.length], { style: { fontSize: 32 } })}
                    </Box>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {feature.title}
                    </Typography>
                    <Typography color="text.secondary">
                        {feature.description}
                    </Typography>
                  </MotionPaper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>

        {/* CTA Section - Scroll Reveal Orchestration */}
        <motion.div 
            variants={itemEnter} 
            initial={shouldSimplify ? "visible" : "hidden"} 
            whileInView="visible" 
            viewport={{ once: true, amount: 0.5 }}
        >
          <Box 
            textAlign="center" 
            mt={{ xs: 10, md: 12 }}
            sx={{
                bgcolor: 'primary.main',
                color: agentState.theme === "theme-cinematic-dark" ? "#171510" : "white",
                p: { xs: 4, md: 6 },
                borderRadius: 3,
            }}
          >
            <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" gutterBottom>
              {t("features.cta.title")}
            </Typography>
            <Typography
              variant={isMobile ? "body1" : "h6"}
              color={agentState.theme === "theme-cinematic-dark" ? "#42361e" : "#e0e7ff"}
              mb={4}
            >
              {t("features.cta.subtitle")}
            </Typography>
            <MotionButton
                variant="contained" 
                color="secondary" 
                size="large"
                // 🧠 AI Sensor: Track high-value intent to convert
                onClick={() => reportBehavior('intent_features_cta_conversion', 5, 'interest')}
                // 🧠 AI Actuator: Trigger the glow effect based on engine state
                className={agentState.ui.highlightCTAs ? 'cta-highlight-glow' : ''}
                
                whileHover={!shouldSimplify ? { scale: 1.05, boxShadow: "0 8px 15px rgba(255, 255, 255, 0.3)" } : {}}
                whileTap={!shouldSimplify ? { scale: 0.95 } : {}}
            >
              {t("features.cta.button")}
            </MotionButton>
          </Box>
        </motion.div>

      </Container>
    </motion.div>
  );
};

export default Features;